import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import AuthLayout from '@/components/auth-layout';
import AuthCard from '@/components/auth-card';
import useConfirmEmail from '@/hooks/use-confirm-email';
import { containerVariants, itemVariants } from '@/lib/animations';

const ConfirmEmail = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const token: string | null = new URLSearchParams(location.search).get('token');
  const { isLoading, isSuccess, isError, error } = useConfirmEmail(token);

  return (
    <AuthLayout>
      <AuthCard>
        <motion.div
          className="flex flex-col items-center gap-4 sm:gap-6 py-4 sm:py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {isLoading && (
            <>
              <motion.div variants={itemVariants} className="flex justify-center">
                <motion.div
                  className="p-3 sm:p-4 rounded-full bg-primary/10"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Mail className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
                </motion.div>
              </motion.div>
              <motion.h2
                variants={itemVariants}
                className="text-lg sm:text-xl md:text-2xl font-semibold text-center"
              >
                {t('loading')}
              </motion.h2>
              <motion.div variants={itemVariants}>
                <Loader2 className="h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 animate-spin text-primary" />
              </motion.div>
            </>
          )}

          {isError && (
            <>
              <motion.h2
                variants={itemVariants}
                className="text-lg sm:text-xl md:text-2xl font-semibold text-center text-destructive"
              >
                {error}
              </motion.h2>
              <motion.div
                variants={itemVariants}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
              >
                <XCircle className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 text-destructive" />
              </motion.div>
            </>
          )}

          {isSuccess && (
            <>
              <motion.div variants={itemVariants} className="flex justify-center">
                <motion.div
                  className="p-3 sm:p-4 rounded-full bg-green-500/10"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                >
                  <Mail className="h-8 w-8 sm:h-10 sm:w-10 text-green-500" />
                </motion.div>
              </motion.div>
              <motion.h2
                variants={itemVariants}
                className="text-lg sm:text-xl md:text-2xl font-semibold text-center"
              >
                {t('emailConfirmed')}
              </motion.h2>
              <motion.p
                variants={itemVariants}
                className="text-sm sm:text-base text-muted-foreground text-center"
              >
                {t('emailConfirmedDescription')}
              </motion.p>
              <motion.div
                variants={itemVariants}
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.3 }}
              >
                <CheckCircle className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 text-green-500" />
              </motion.div>
            </>
          )}
        </motion.div>
      </AuthCard>
    </AuthLayout>
  );
}

export default ConfirmEmail;